import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

import { API_get_file_upload_document_images } from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";

/**
 * Gallery of the photos extracted from one knowledge-hub document
 * (PDF/DOCX/PPTX embeds, or the source page of a URL import). Until
 * this surface existed, extraction was only visible inside the
 * editors' media-library pickers — managers had no way to see whether
 * it ran or what it found.
 *
 * `images_extracted_at` distinguishes the empty states: null means the
 * async extraction hasn't processed this document yet; set with zero
 * rows means it ran and found nothing usable.
 */
export default function ExtractedImagesModal({ open, onClose, companyId, file }) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [images, setImages] = React.useState([]);
  const [extractedAt, setExtractedAt] = React.useState(null);

  React.useEffect(() => {
    if (!open || !file?.uuid) return;
    let cancelled = false;
    async function load() {
      setImages([]);
      setExtractedAt(null);
      setLoading(true);
      setError(false);
      try {
        const res = await getRightworkAPI(
          API_get_file_upload_document_images,
          { company_id: companyId, file_upload_uuid: file.uuid },
        );
        if (cancelled) return;
        const payload = res?.data?.redux?.payload;
        setImages(payload?.images || []);
        setExtractedAt(payload?.images_extracted_at || null);
      } catch (err) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, file?.uuid]);

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="extracted-images-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(720px, 94vw)",
          maxHeight: "min(80vh, 680px)",
          bgcolor: "background.paper",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <h2
              id="extracted-images-title"
              className="text-base font-semibold text-slate-900"
            >
              Extracted photos
            </h2>
            <p className="text-xs text-slate-500 truncate">
              {file?.displayName || file?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 text-slate-500"
            aria-label="Close"
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-12 text-center">
              <CircularProgress size={24} sx={{ color: "#6d28d9" }} />
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-rose-600">
              Couldn't load photos. Close and reopen to retry.
            </div>
          ) : images.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500 px-8">
              {extractedAt
                ? "No usable photos were found in this document."
                : "Photos haven't been extracted from this document yet — extraction runs automatically a few minutes after upload, for documents in folders with assistant search enabled."}
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-3">
                {images.length} photo{images.length === 1 ? "" : "s"} —
                reusable in training content via the editors' media
                library.
              </p>
              <div className="grid grid-cols-3 md:grid-cols-2 gap-3">
                {images.map((img) => (
                  <GalleryTile key={img.uuid} image={img} />
                ))}
              </div>
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
}

function GalleryTile({ image }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
      <div className="aspect-[4/3] bg-slate-100 relative">
        {image.url && !imgFailed ? (
          <img
            src={image.url}
            alt={image.caption || ""}
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">
            Preview unavailable
          </div>
        )}
      </div>
      {image.caption && (
        <p className="px-2 py-1.5 text-[11px] text-slate-700 line-clamp-2 leading-snug">
          {image.caption}
        </p>
      )}
    </div>
  );
}
